import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Icon, Loading, PageHeader, ListboxSelect } from '@evertrue/et-components';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import EverTrue from 'app';
import { useIdentity } from 'base/identity-resolver';
import { fetchTasksByAppliedFilters } from '../utils/fetch-helpers';
import { setTaskCompletion } from '../utils/tasks-actions';
import useCadenceMutations from 'apps/cadences-v2/hooks/use-cadence-mutations';
import useFetchAllUsers from 'apps/search/hooks/use-fetch-all-users';
import SessionStore from 'base/session/session-store';
import { useGlobalDrawerProvider } from 'components/drawer/context/global-drawer-context';
import { trackOutreachAction } from 'apps/outreach/utils/utils';
import AddTask from 'apps/tasks/components/create/add-task';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';

const COMPLETED_PAGE_SIZE = 200;
const FETCH_PAGE_SIZE = 200;

/** Fetch all tasks matching the given filters by paging through results. */
const fetchAllTasks = async (filters, options = {}) => {
  let allItems = [];
  let offset = 0;
  let total = 0;

  for (;;) {
    const resp = await fetchTasksByAppliedFilters(
      {
        ...filters,
        limit: FETCH_PAGE_SIZE,
        offset,
      },
      options
    );
    const items = resp?.items || [];
    total = resp?.total || 0;
    allItems = allItems.concat(items);
    offset += items.length;
    if (items.length < FETCH_PAGE_SIZE || offset >= total) break;
  }

  return { items: allItems, total };
};

/** Parse a date string, treating date-only strings (YYYY-MM-DD) as local dates.
 *  new Date("2026-03-12") parses as UTC midnight per the JS spec, which
 *  shifts the date in local timezone. This parses date-only strings as local midnight instead. */
const parseLocalDate = (str) => {
  if (typeof str === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(str);
};

/** Format a date as YYYY-MM-DD in local timezone. */
const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Determine whether a task is overdue (due before today, not completed). */
const isOverdue = (task) => {
  if (task.completed_at) return false;
  if (!task.current_due_date) return false;
  const due = parseLocalDate(task.current_due_date);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

/** Determine whether a task is due today. */
const isDueToday = (task) => {
  if (!task.current_due_date) return false;
  return toDateStr(parseLocalDate(task.current_due_date)) === toDateStr(new Date());
};

/** Map action_type enum to a readable label. */
const formatActionType = (actionType) => {
  if (!actionType) return 'Task';
  return actionType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

/** Get the last N days as an array of { date, label } objects. */
const getLastNDays = (n) => {
  const days = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push({
      date: toDateStr(d),
      label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      shortLabel: d.toLocaleDateString('en-US', { weekday: 'short' }),
    });
  }
  return days;
};

const TIMEFRAME_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'This week', value: 'week' },
  { label: 'This month', value: 'month' },
  { label: 'This year', value: 'year' },
  { label: 'All time', value: 'all' },
];

/** Filter tasks by timeframe based on completed_at (epoch seconds). */
const filterByTimeframe = (tasks, timeframe) => {
  if (timeframe === 'all') return tasks;
  const now = new Date();
  let start;
  switch (timeframe) {
    case 'today':
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      break;
    case 'week':
      start = new Date(now);
      start.setDate(now.getDate() - 7);
      break;
    case 'month':
      start = new Date(now);
      start.setMonth(now.getMonth() - 1);
      break;
    case 'year':
      start = new Date(now);
      start.setFullYear(now.getFullYear() - 1);
      break;
    default:
      return tasks;
  }
  return tasks.filter((t) => {
    if (!t.completed_at) return false;
    return new Date(t.completed_at * 1000) >= start;
  });
};

/**
 * Tasks Summary Controller — shows task lists, stat cards, and completion chart.
 * Combines Cadence 1.0 (journeys) and Cadence 2.0 (cadences-v2) tasks.
 *
 * The top task cards always show the current user's tasks.
 * The bottom Data Summary section reacts to the user picker.
 */
const TasksSummaryController = () => {
  const {
    user: { id: userId },
  } = useIdentity();

  // Top section state — always shows current user's tasks
  const [topLoading, setTopLoading] = useState(true);
  const [myDueTasks, setMyDueTasks] = useState([]);
  const [myCadenceTasks, setMyCadenceTasks] = useState([]);

  // Bottom Data Summary state — changes with user picker
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [selectedDueTasks, setSelectedDueTasks] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [cadence1CompletedCount, setCadence1CompletedCount] = useState(0);
  const [allCompletedTasks, setAllCompletedTasks] = useState([]);
  const [completedFetchTotal, setCompletedFetchTotal] = useState(0);
  const completedFetchingMore = useRef(false);
  const completedLoadedRef = useRef(0);

  // User picker state
  const [selectedUserId, setSelectedUserId] = useState(null);
  // Pinned option preserves the selected user's label/value even after the users list is
  // reset by a new search (which would otherwise cause selectedUserOption to fall back to Me).
  const [pinnedUserOption, setPinnedUserOption] = useState(null);
  const { users, fetchUsers } = useFetchAllUsers();
  const currentUser = SessionStore.getUser();
  const searchTimeoutRef = useRef(null);

  // Timeframe states
  const [cadenceTimeframe, setCadenceTimeframe] = useState('week');
  const [nonCadenceTimeframe, setNonCadenceTimeframe] = useState('week');
  const [chartTimeframe, setChartTimeframe] = useState('week');

  const { completeManualStep } = useCadenceMutations();
  const { addDrawer } = useGlobalDrawerProvider();

  // Task modal state (reuses AddTask modal from tasks table)
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);

  // Show-more limits for task lists (display 10, click to reveal more)
  const INITIAL_SHOW = 10;
  const SHOW_MORE_INCREMENT = 20;
  const [dueShowCount, setDueShowCount] = useState(INITIAL_SHOW);
  const [cadenceShowCount, setCadenceShowCount] = useState(INITIAL_SHOW);

  // Cadence name lookup for v2 tasks
  const [cadenceNameMap, setCadenceNameMap] = useState({});

  // Automated event counts by date key — from cadence activity data (email sends)
  const [automatedByDate, setAutomatedByDate] = useState({});

  // The automated bar requires a fan-out of one GraphQL request per cadence with
  // automated events, which is expensive for orgs with many cadences. Defer that
  // work until the chart actually scrolls into view.
  const chartSectionRef = useRef(null);
  const [chartVisible, setChartVisible] = useState(false);

  useEffect(() => {
    if (chartVisible) return;
    if (typeof IntersectionObserver === 'undefined') {
      // No observer available (test env, old browser) — load immediately.
      setChartVisible(true);
      return;
    }
    // The trends section is only rendered after summaryLoading flips to false,
    // so the ref may be null on the first run. Wait — the effect re-runs when
    // summaryLoading changes (deps below) and we'll attach the observer then.
    const el = chartSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setChartVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [chartVisible, summaryLoading]);

  useEffect(() => {
    const v2Tasks = myCadenceTasks.filter((t) => t.task_source === 'cadence_v2' && t.cadence_id);
    if (v2Tasks.length === 0) return;
    let cancelled = false;
    cadenceGqlCall('query Cadences { cadences { id name events { id sequenceOrder eventType } } }')
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data?.cadences || []).forEach((c) => {
          const eventSteps = {};
          let stepNum = 0;
          (c.events || [])
            .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
            .forEach((e) => {
              const t = (e.eventType || '').toLowerCase();
              if (!['start', 'end', 'wait', 'condition'].includes(t)) stepNum++;
              eventSteps[e.id] = stepNum;
            });
          map[c.id] = { name: c.name, eventSteps };
        });
        setCadenceNameMap(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [myCadenceTasks]);

  // Fetch automated event data from cadence activities for the chart.
  // Scoped to cadences created by the selected user so the automated bar reflects
  // the same person being viewed (consistent with the manual tasks bars).
  // Gated on chartVisible so the N+1 fan-out doesn't fire until the chart is in view.
  useEffect(() => {
    if (!selectedUserId || !chartVisible) return;
    let cancelled = false;

    const isViewingTeammate = selectedUserId !== userId;
    cadenceGqlCall(
      `query Cadences($viewUserId: ID) { cadences(viewUserId: $viewUserId) { id status createdByUserId ownerUserId events { id isAutomated eventType } } }`,
      isViewingTeammate ? { viewUserId: String(selectedUserId) } : {}
    )
      .then((data) => {
        if (cancelled) return null;
        const cadences = (data?.cadences || []).filter((c) => c.status?.toLowerCase() !== 'draft');
        if (cadences.length === 0) {
          setAutomatedByDate({});
          return null;
        }

        // Build set of automated event IDs per cadence
        const automatedEvents = new Map();
        cadences.forEach((c) => {
          const autoIds = new Set();
          (c.events || []).forEach((e) => {
            if (e.isAutomated) autoIds.add(String(e.id));
          });
          if (autoIds.size > 0) automatedEvents.set(c.id, autoIds);
        });

        // Fetch activities for cadences that have automated events
        return Promise.allSettled(
          [...automatedEvents.keys()].map((cadenceId) =>
            cadenceGqlCall(
              `query RecentCadenceActivity($cadenceId: ID!) {
                recentCadenceActivity(cadenceId: $cadenceId) {
                  edges { node { activityType activityAt cadenceEventId } }
                }
              }`,
              { cadenceId: String(cadenceId) }
            ).then((result) => ({
              cadenceId,
              activities: result?.recentCadenceActivity?.edges?.map((e) => e.node) || [],
            }))
          )
        ).then((results) => {
          if (cancelled) return;
          const byDate = {};
          results.forEach((r) => {
            if (r.status !== 'fulfilled') return;
            const { cadenceId, activities } = r.value;
            const autoIds = automatedEvents.get(cadenceId);
            activities.forEach((a) => {
              if (a.activityType !== 'email_sent') return;
              if (!autoIds.has(String(a.cadenceEventId))) return;
              if (!a.activityAt) return;
              // activityAt is ISO string e.g. "2026-03-06T19:21:58"
              const monthKey = a.activityAt.slice(0, 7); // "YYYY-MM"
              const dayKey = a.activityAt.slice(0, 10); // "YYYY-MM-DD"
              byDate[monthKey] = (byDate[monthKey] || 0) + 1;
              byDate[dayKey] = (byDate[dayKey] || 0) + 1;
            });
          });
          setAutomatedByDate(byDate);
        });
      })
      .catch(() => {
        if (!cancelled) setAutomatedByDate({});
      });

    return () => {
      cancelled = true;
    };
  }, [selectedUserId, userId, chartVisible]);

  // Initialize selectedUserId when userId becomes available
  useEffect(() => {
    if (userId && !selectedUserId) {
      setSelectedUserId(userId);
    }
  }, [userId, selectedUserId]);

  // Derive cadence task subtitle count (top section — always current user)
  const cadenceTasksSubtitle = useMemo(() => {
    const overdueNum = myCadenceTasks.filter(isOverdue).length;
    const todayNum = myCadenceTasks.filter(isDueToday).length;
    if (myCadenceTasks.length === 0) return 'No active cadence tasks';
    return `${overdueNum} overdue, ${todayNum} due today`;
  }, [myCadenceTasks]);

  // User picker options
  const userOptions = useMemo(() => {
    const meOption = {
      label: 'Me',
      value: userId,
      searchLabel: currentUser?.name || 'Me',
      subtitle: 'You',
    };
    const otherUsers = users
      .filter((u) => u.id !== userId)
      .map((u) => ({
        label: u.name,
        value: u.id,
        subtitle: u.title || u.role || '',
      }));
    return [meOption, ...otherUsers];
  }, [users, userId, currentUser]);

  const selectedUserOption = useMemo(() => {
    return userOptions.find((o) => o.value === selectedUserId) || pinnedUserOption || userOptions[0];
  }, [userOptions, selectedUserId, pinnedUserOption]);

  // Top section: always loads for current user (not affected by user picker)
  useEffect(() => {
    if (!userId) return;
    setTopLoading(true);

    fetchAllTasks({
      owner_user_id: userId,
      statuses: ['IN_PROGRESS', 'PAUSED'],
      sort: ['current_due_date', 'ASC'],
    })
      .then((incompleteResp) => {
        const allIncomplete = incompleteResp?.items || [];
        const isCadenceTask = (t) => t.metadata?.journey_id || t.task_source === 'cadence_v2';
        // Non-cadence tasks: exclude cadence tasks, filter to due today/overdue
        const nonCadenceDue = allIncomplete
          .filter((t) => !isCadenceTask(t))
          .filter((t) => isDueToday(t) || isOverdue(t));
        setMyDueTasks(nonCadenceDue);
        setDueShowCount(INITIAL_SHOW);
        // Cadence tasks (both 1.0 and 2.0): filter to due today/overdue only
        const cadenceDue = allIncomplete.filter(isCadenceTask).filter((t) => isDueToday(t) || isOverdue(t));
        setMyCadenceTasks(cadenceDue);
        setCadenceShowCount(INITIAL_SHOW);
      })
      .catch(() => {
        EverTrue.Alert.error('Failed to load tasks');
      })
      .finally(() => {
        setTopLoading(false);
      });
  }, [userId]);

  // Bottom Data Summary: loads for selected user (reacts to user picker).
  // When viewing yourself, the top-section effect already loaded incomplete tasks —
  // skip the redundant IN_PROGRESS fetch and derive selectedDueTasks from those instead.
  useEffect(() => {
    if (!selectedUserId) return;
    const isViewingSelf = selectedUserId === userId;
    setSummaryLoading(true);
    completedLoadedRef.current = 0;
    setCompletedFetchTotal(0);

    // Data summary widgets only show counts/aggregates — skip the per-task
    // creator/owner enrichment that fetchTasksByAppliedFilters does by default.
    const summaryOpts = { skipUserEnrichment: true };
    const requests = [
      // Total completed cadence 1.0 tasks (backend-filtered) — limit:1 because we only need .total
      fetchTasksByAppliedFilters(
        {
          owner_user_id: selectedUserId,
          statuses: ['COMPLETED'],
          cadence_task: true,
          limit: 1,
          offset: 0,
        },
        summaryOpts
      ),
      // Completed tasks for chart + timeframe filtering (first page — more loaded on demand).
      // The .total from this response is also used as the overall completed count.
      fetchTasksByAppliedFilters(
        {
          owner_user_id: selectedUserId,
          statuses: ['COMPLETED'],
          sort: ['current_due_date', 'DESC'],
          limit: COMPLETED_PAGE_SIZE,
          offset: 0,
        },
        summaryOpts
      ),
    ];
    // When viewing a teammate, also fetch their active tasks for the DUE + OVERDUE card.
    // (When viewing self, this is computed from the top-section data instead.)
    if (!isViewingSelf) {
      requests.push(
        fetchAllTasks(
          {
            owner_user_id: selectedUserId,
            statuses: ['IN_PROGRESS', 'PAUSED'],
            sort: ['current_due_date', 'ASC'],
          },
          summaryOpts
        )
      );
    }

    Promise.all(requests)
      .then(([cadence1Resp, recentCompleted, dueResp]) => {
        const allCompleted = recentCompleted?.items || [];
        const totalCompleted = recentCompleted?.total || 0;

        setCompletedCount(totalCompleted);
        setCadence1CompletedCount(cadence1Resp?.total || 0);
        setAllCompletedTasks(allCompleted);
        setCompletedFetchTotal(totalCompleted);
        completedLoadedRef.current = allCompleted.length;

        if (!isViewingSelf) {
          const activeTasks = dueResp?.items || [];
          setSelectedDueTasks(activeTasks.filter((t) => isDueToday(t) || isOverdue(t)));
        }
      })
      .catch(() => {
        EverTrue.Alert.error('Failed to load data summary');
      })
      .finally(() => {
        setSummaryLoading(false);
      });
  }, [selectedUserId, userId]);

  // When viewing self, selectedDueTasks is the union of top-section due lists.
  useEffect(() => {
    if (selectedUserId && selectedUserId === userId) {
      setSelectedDueTasks([...myDueTasks, ...myCadenceTasks]);
    }
  }, [selectedUserId, userId, myDueTasks, myCadenceTasks]);

  /** Fetch the next page of completed tasks and append to allCompletedTasks. */
  const fetchMoreCompleted = useCallback(async () => {
    const targetUserId = selectedUserId || userId;
    if (!targetUserId || completedFetchingMore.current) return false;
    if (completedLoadedRef.current >= completedFetchTotal) return false;

    completedFetchingMore.current = true;
    try {
      const resp = await fetchTasksByAppliedFilters(
        {
          owner_user_id: targetUserId,
          statuses: ['COMPLETED'],
          sort: ['current_due_date', 'DESC'],
          limit: COMPLETED_PAGE_SIZE,
          offset: completedLoadedRef.current,
        },
        { skipUserEnrichment: true }
      );
      const newItems = resp?.items || [];
      if (newItems.length > 0) {
        completedLoadedRef.current += newItems.length;
        setAllCompletedTasks((prev) => [...prev, ...newItems]);
      }
      return newItems.length === COMPLETED_PAGE_SIZE;
    } catch {
      return false;
    } finally {
      completedFetchingMore.current = false;
    }
  }, [selectedUserId, userId, completedFetchTotal]);

  // Progressively load all completed tasks when "All time" is active
  useEffect(() => {
    const needsAll = cadenceTimeframe === 'all' || nonCadenceTimeframe === 'all' || chartTimeframe === 'all';
    if (!needsAll || completedFetchTotal === 0) return;
    if (completedLoadedRef.current >= completedFetchTotal) return;

    let cancelled = false;
    const loadAll = async () => {
      while (!cancelled && completedLoadedRef.current < completedFetchTotal) {
        const hasMore = await fetchMoreCompleted();
        if (!hasMore) break;
      }
    };
    loadAll();

    return () => {
      cancelled = true;
    };
  }, [cadenceTimeframe, nonCadenceTimeframe, chartTimeframe, completedFetchTotal, fetchMoreCompleted]);

  // Computed: filtered completed counts for stat card timeframes
  const isCadenceTask = (t) => t.metadata?.journey_id || t.task_source === 'cadence_v2';

  // Derive cadence completed count from loaded tasks + backend cadence 1.0 total.
  // cadence 1.0 tasks have metadata.journey_id; cadence 2.0 tasks have task_source='cadence_v2'.
  // The backend cadence_task filter only covers 1.0, so we count 2.0 from the loaded list.
  // As progressive loading fetches more pages, this recomputes automatically.
  const cadenceCompletedCount = useMemo(() => {
    const cadence2Count = allCompletedTasks.filter((t) => t.task_source === 'cadence_v2').length;
    return cadence1CompletedCount + cadence2Count;
  }, [allCompletedTasks, cadence1CompletedCount]);

  const filteredCadenceCompleted = useMemo(() => {
    if (cadenceTimeframe === 'all') return cadenceCompletedCount;
    const cadenceTasks = allCompletedTasks.filter(isCadenceTask);
    return filterByTimeframe(cadenceTasks, cadenceTimeframe).length;
  }, [allCompletedTasks, cadenceTimeframe, cadenceCompletedCount]);

  const filteredNonCadenceCompleted = useMemo(() => {
    if (nonCadenceTimeframe === 'all') return completedCount - cadenceCompletedCount;
    const nonCadenceTasks = allCompletedTasks.filter((t) => !isCadenceTask(t));
    return filterByTimeframe(nonCadenceTasks, nonCadenceTimeframe).length;
  }, [allCompletedTasks, nonCadenceTimeframe, completedCount, cadenceCompletedCount]);

  // Chart data for recharts — uses day-level bars for short timeframes, month-level for year/all
  const chartData = useMemo(() => {
    const filteredTasks = filterByTimeframe(allCompletedTasks, chartTimeframe);

    // For year/all-time, group by month; otherwise group by day
    const useMonths = chartTimeframe === 'year' || chartTimeframe === 'all';

    if (useMonths) {
      const months = {};
      const now = new Date();
      let startMonth;
      if (chartTimeframe === 'year') {
        startMonth = new Date(now.getFullYear(), 0, 1);
      } else {
        // "All time" — start from earliest completed task, minimum 12 months back
        let earliest = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        filteredTasks.forEach((t) => {
          if (!t.completed_at) return;
          const d = new Date(t.completed_at * 1000);
          if (d < earliest) earliest = new Date(d.getFullYear(), d.getMonth(), 1);
        });
        startMonth = earliest;
      }
      for (let d = new Date(startMonth); d <= now; d.setMonth(d.getMonth() + 1)) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        months[key] = {
          name: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          fullDate: key,
          manual: 0,
          automated: 0,
        };
      }
      filteredTasks.forEach((t) => {
        if (!t.completed_at) return;
        const d = new Date(t.completed_at * 1000);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (months[key]) {
          months[key].manual++;
        }
      });
      // Automated counts from cadence activity data (email sends by month)
      Object.keys(months).forEach((key) => {
        months[key].automated = automatedByDate[key] || 0;
      });
      return Object.values(months);
    }

    // Day-level bars for today/week/month
    const numDays = chartTimeframe === 'month' ? 30 : chartTimeframe === 'week' ? 7 : 1;
    const days = getLastNDays(numDays);
    return days.map((day) => {
      let manualCount = 0;
      filteredTasks.forEach((t) => {
        if (!t.completed_at) return;
        if (toDateStr(new Date(t.completed_at * 1000)) === day.date) {
          manualCount++;
        }
      });
      return {
        name: numDays === 1 ? 'Today' : day.shortLabel,
        fullDate: day.date,
        manual: manualCount,
        automated: automatedByDate[day.date] || 0,
      };
    });
  }, [chartTimeframe, allCompletedTasks, automatedByDate]);

  const handleCompleteTask = async (task) => {
    try {
      await setTaskCompletion(task, true);
      setMyDueTasks((prev) => prev.filter((t) => t.id !== task.id));
      setMyCadenceTasks((prev) => prev.filter((t) => t.id !== task.id));

      // If viewing own data, update summary counts too
      if (!selectedUserId || selectedUserId === userId) {
        setSelectedDueTasks((prev) => prev.filter((t) => t.id !== task.id));
        setCompletedCount((c) => c + 1);
      }

      if (task.task_source === 'cadence_v2' && task.cadence_member_id && task.cadence_event_id) {
        try {
          await completeManualStep(task.cadence_member_id, task.cadence_event_id);
        } catch (err) {
          console.error('Failed to signal cadence workflow:', err);
          EverTrue.Alert.warn(
            'Task marked complete, but the cadence workflow could not be advanced. ' +
              'Please try completing the task again or contact support.'
          );
        }
      }
    } catch {
      // setTaskCompletion already shows an alert
    }
  };

  const handleViewAllManual = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dueBefore = tomorrow.toISOString().slice(0, 10);
    EverTrue.Navigator(`/tasks?taskTypes=non_cadence&status=incomplete&dueDateBefore=${dueBefore}`, true);
  };

  const handleViewAllCadence = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dueBefore = tomorrow.toISOString().slice(0, 10);
    EverTrue.Navigator(`/tasks?taskTypes=cadence_1,cadence_2&status=incomplete&dueDateBefore=${dueBefore}`, true);
  };

  const handleViewContact = (contactId) => {
    if (contactId) {
      EverTrue.Navigator(`/contact/${contactId}`, true);
    }
  };

  const getTaskIcon = (actionType) => {
    const type = (actionType || '').toUpperCase();
    if (type === 'EMAIL') return { icon: 'email', iconKit: 'gt' };
    if (type === 'CALL') return { icon: 'call', iconKit: 'gt' };
    return { icon: 'other-tasks-cadences', iconKit: 'gt2' };
  };

  const getTaskIconColor = (actionType) => {
    const type = (actionType || '').toUpperCase();
    if (type === 'CALL') return '#e91e63';
    if (type === 'EMAIL') return '#8e44ad';
    return '#6b7785';
  };

  const getContactName = (task) => {
    const attrs = task.contact?.contact_attributes;
    return attrs?.name_full || [attrs?.name_first, attrs?.name_last].filter(Boolean).join(' ') || 'Unknown';
  };

  // Top section counts (always current user)
  const myOverdueCount = myDueTasks.filter(isOverdue).length;
  const myDueTodayCount = myDueTasks.filter(isDueToday).length;

  // Bottom section counts (selected user)
  const selectedOverdueCount = selectedDueTasks.filter(isOverdue).length;
  const selectedDueTodayCount = selectedDueTasks.filter(isDueToday).length;
  const selectedTotalDueCount = selectedDueTodayCount + selectedOverdueCount;

  /** Handle clicking the action-type icon — opens emailer, dialer, etc. */
  const handleTaskIconClick = (task) => {
    const { action_type, contact, id } = task;
    if (!contact || !action_type) return;

    const cAttrs = contact?.contact_attributes;
    const name =
      cAttrs?.name_full?.trim() ||
      [cAttrs?.name_first, cAttrs?.name_last].filter(Boolean).join(' ').trim() ||
      cAttrs?.name_nick?.trim() ||
      null;
    const title = name ?? 'New Message';

    if (action_type === 'EMAIL') {
      trackOutreachAction('open_emailer');
      addDrawer('emailer', { contact, taskId: id, name, drawerTitle: title });
    } else if (action_type === 'CALL') {
      trackOutreachAction('open_dialer');
      addDrawer('dialer', { contact, phones: contact.phones || [], name, drawerTitle: title });
    }
  };

  /** Render a task item row with circle checkbox, icon, badge, title, and contact. */
  const renderTaskItem = (task, { showCadenceName = false } = {}) => {
    const contactName = getContactName(task);
    const isV2 = task.task_source === 'cadence_v2';
    const isV1 = !isV2 && !!task.metadata?.journey_id;
    const isCadenceTask = isV2 || isV1;
    const cadenceName = task.metadata?.journey_name || (isV2 ? cadenceNameMap[task.cadence_id]?.name || null : '');

    return (
      <div key={task.id} className="tasks-summary-page--task-item">
        <button
          type="button"
          className="tasks-summary-page--circle-checkbox"
          onClick={(e) => {
            e.stopPropagation();
            handleCompleteTask(task);
          }}
          title="Complete task"
        >
          <Icon icon="check" />
        </button>
        {['EMAIL', 'CALL'].includes(task.action_type) && task.contact ? (
          <button
            type="button"
            className="tasks-summary-page--task-icon tasks-summary-page--task-icon-clickable"
            style={{
              backgroundColor: `${getTaskIconColor(task.action_type)}15`,
              color: getTaskIconColor(task.action_type),
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleTaskIconClick(task);
            }}
            title={task.action_type === 'EMAIL' ? 'Open emailer' : 'Open dialer'}
          >
            <Icon icon={getTaskIcon(task.action_type).icon} iconKit={getTaskIcon(task.action_type).iconKit} />
          </button>
        ) : (
          <div
            className="tasks-summary-page--task-icon"
            style={{
              backgroundColor: `${getTaskIconColor(task.action_type)}15`,
              color: getTaskIconColor(task.action_type),
            }}
          >
            <Icon icon={getTaskIcon(task.action_type).icon} iconKit={getTaskIcon(task.action_type).iconKit} />
          </div>
        )}
        <div className="tasks-summary-page--task-content">
          <div className="tasks-summary-page--task-header">
            {isOverdue(task) && (
              <span className="tasks-summary-page--task-badge tasks-summary-page--task-badge-overdue">OVERDUE</span>
            )}
            {isDueToday(task) && !isOverdue(task) && (
              <span className="tasks-summary-page--task-badge tasks-summary-page--task-badge-today">TODAY</span>
            )}
            {showCadenceName && isCadenceTask && (
              <span
                className={`tasks-summary-page--cadence-pill ${isV2 ? 'tasks-summary-page--cadence-pill-v2' : 'tasks-summary-page--cadence-pill-v1'}`}
              >
                {isV2
                  ? cadenceName || (Object.keys(cadenceNameMap).length > 0 ? 'Cadence Removed' : '')
                  : isV1
                    ? cadenceName || '1.0'
                    : 'Task'}
              </span>
            )}
          </div>
          <div className="tasks-summary-page--task-title">{task.title || formatActionType(task.action_type)}</div>
          <button
            type="button"
            className="tasks-summary-page--task-contact-link"
            onClick={(e) => {
              e.stopPropagation();
              handleViewContact(task.target_contact_id);
            }}
          >
            {contactName}
          </button>
        </div>
        <button
          type="button"
          className="tasks-summary-page--view-btn"
          onClick={(e) => {
            e.stopPropagation();
            const isV2 = task.task_source === 'cadence_v2';
            const info = isV2 && task.cadence_id ? cadenceNameMap[task.cadence_id] : null;
            const enriched = info
              ? {
                  ...task,
                  _cadenceName: info.name,
                  _stepNumber: task.cadence_event_id ? info.eventSteps?.[task.cadence_event_id] || null : null,
                }
              : task;
            setSelectedTask(enriched);
            setTaskModalOpen(true);
          }}
        >
          View
        </button>
      </div>
    );
  };

  return (
    <div className="tasks-summary-page">
      <div className="tasks-summary-page--header">
        <PageHeader title="Tasks Summary" subtitle="Manage and track your outreach tasks" />
      </div>

      {topLoading ? (
        <div className="tasks-summary-page--loading">
          <Loading />
        </div>
      ) : (
        <div className="tasks-summary-page--content">
          {/* Two-column task cards (top) — always current user */}
          <div className="tasks-summary-page--tasks-grid">
            {/* Non-Cadence Tasks (left column) */}
            <div className="tasks-summary-page--tasks-section">
              <div className="tasks-summary-page--section-header">
                <div>
                  <h2 className="tasks-summary-page--section-title">Non-Cadence Tasks</h2>
                  <p className="tasks-summary-page--section-subtitle">
                    {myDueTasks.length > 0
                      ? `${myOverdueCount} overdue, ${myDueTodayCount} due today`
                      : 'No tasks due today \u2014 you are all caught up!'}
                  </p>
                </div>
                <Button type="simple" onClick={handleViewAllManual}>
                  View All
                </Button>
              </div>
              <div className="tasks-summary-page--tasks-list">
                {myDueTasks.length === 0 && (
                  <div className="tasks-summary-page--empty">
                    <Icon icon="check-circle" />
                    <span>No tasks due today</span>
                  </div>
                )}
                {myDueTasks.slice(0, dueShowCount).map((task) => renderTaskItem(task))}
                {myDueTasks.length > dueShowCount && (
                  <button
                    type="button"
                    className="tasks-summary-page--show-more"
                    onClick={() => setDueShowCount((c) => c + SHOW_MORE_INCREMENT)}
                  >
                    Show More ({myDueTasks.length - dueShowCount} remaining)
                  </button>
                )}
              </div>
            </div>

            {/* Cadence 1.0 + 2.0 Tasks (right column) */}
            <div className="tasks-summary-page--tasks-section">
              <div className="tasks-summary-page--section-header">
                <div>
                  <h2 className="tasks-summary-page--section-title">Cadence 1.0 + 2.0 Tasks</h2>
                  <p className="tasks-summary-page--section-subtitle">{cadenceTasksSubtitle}</p>
                </div>
                <Button type="simple" onClick={handleViewAllCadence}>
                  View All
                </Button>
              </div>
              <div className="tasks-summary-page--tasks-list">
                {myCadenceTasks.length === 0 && (
                  <div className="tasks-summary-page--empty">
                    <Icon icon="check-circle" />
                    <span>No active cadence tasks</span>
                  </div>
                )}
                {myCadenceTasks
                  .slice(0, cadenceShowCount)
                  .map((task) => renderTaskItem(task, { showCadenceName: true }))}
                {myCadenceTasks.length > cadenceShowCount && (
                  <button
                    type="button"
                    className="tasks-summary-page--show-more"
                    onClick={() => setCadenceShowCount((c) => c + SHOW_MORE_INCREMENT)}
                  >
                    Show More ({myCadenceTasks.length - cadenceShowCount} remaining)
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Data Summary section — reacts to user picker */}
          <div className="tasks-summary-page--data-summary">
            <h2 className="tasks-summary-page--data-summary-title">Data Summary</h2>

            <div className="tasks-summary-page--user-picker">
              <div className="tasks-summary-page--user-picker-label">View Data For</div>
              <ListboxSelect
                size="medium"
                options={userOptions}
                value={selectedUserOption}
                onChange={(option) => {
                  setSelectedUserId(option.value);
                  setPinnedUserOption(option);
                }}
                onSearch={(search) => {
                  clearTimeout(searchTimeoutRef.current);
                  searchTimeoutRef.current = setTimeout(() => {
                    fetchUsers(search, 0);
                  }, 500);
                }}
                scrollFetch={(search) => {
                  fetchUsers(search, users.length);
                }}
                emptyValue="Me"
                label="View Data For"
              />
            </div>

            {summaryLoading ? (
              <div className="tasks-summary-page--loading">
                <Loading />
              </div>
            ) : (
              <>
                {/* Stat Cards */}
                <div className="tasks-summary-page--stats">
                  <div className="tasks-summary-page--stat-card">
                    <div className="tasks-summary-page--stat-info">
                      <span className="tasks-summary-page--stat-label">DUE + OVERDUE</span>
                      <span className="tasks-summary-page--stat-value">
                        {selectedTotalDueCount} {selectedTotalDueCount === 1 ? 'Task' : 'Tasks'}
                      </span>
                      <span className="tasks-summary-page--stat-sublabel-muted">
                        {selectedDueTodayCount} due today, {selectedOverdueCount} overdue
                      </span>
                    </div>
                    <div className="tasks-summary-page--stat-icon tasks-summary-page--stat-icon-yellow">
                      <Icon icon="access-time" />
                    </div>
                  </div>

                  <div className="tasks-summary-page--stat-card">
                    <div className="tasks-summary-page--stat-info">
                      <span className="tasks-summary-page--stat-label">MANUAL CADENCE TASKS COMPLETED</span>
                      <span className="tasks-summary-page--stat-value">
                        {filteredCadenceCompleted.toLocaleString()}
                      </span>
                      <div className="tasks-summary-page--stat-timeframe">
                        <ListboxSelect
                          size="dynamic"
                          options={TIMEFRAME_OPTIONS}
                          value={TIMEFRAME_OPTIONS.find((o) => o.value === cadenceTimeframe)}
                          onChange={(option) => setCadenceTimeframe(option.value)}
                          emptyValue="All time"
                          label="TIMEFRAME:"
                          alwaysShowLabel={true}
                        />
                      </div>
                    </div>
                    <div className="tasks-summary-page--stat-icon tasks-summary-page--stat-icon-green">
                      <Icon icon="check-circle" />
                    </div>
                  </div>

                  <div className="tasks-summary-page--stat-card">
                    <div className="tasks-summary-page--stat-info">
                      <span className="tasks-summary-page--stat-label">MANUAL NON-CADENCE TASKS COMPLETED</span>
                      <span className="tasks-summary-page--stat-value">
                        {filteredNonCadenceCompleted.toLocaleString()}
                      </span>
                      <div className="tasks-summary-page--stat-timeframe">
                        <ListboxSelect
                          size="dynamic"
                          options={TIMEFRAME_OPTIONS}
                          value={TIMEFRAME_OPTIONS.find((o) => o.value === nonCadenceTimeframe)}
                          onChange={(option) => setNonCadenceTimeframe(option.value)}
                          emptyValue="All time"
                          label="TIMEFRAME:"
                          alwaysShowLabel={true}
                        />
                      </div>
                    </div>
                    <div className="tasks-summary-page--stat-icon tasks-summary-page--stat-icon-gray">
                      <Icon icon="person" />
                    </div>
                  </div>
                </div>

                {/* Task Completion Trends Chart */}
                <div className="tasks-summary-page--trends-section" ref={chartSectionRef}>
                  <div className="tasks-summary-page--trends-header">
                    <div>
                      <h2 className="tasks-summary-page--section-title">Task Completion Trends</h2>
                      <p className="tasks-summary-page--chart-subtitle">Manual vs Automated task completions</p>
                    </div>
                    <div className="tasks-summary-page--chart-timeframe">
                      <ListboxSelect
                        size="dynamic"
                        options={TIMEFRAME_OPTIONS}
                        value={TIMEFRAME_OPTIONS.find((o) => o.value === chartTimeframe)}
                        onChange={(option) => setChartTimeframe(option.value)}
                        emptyValue="All time"
                        label="TIMEFRAME:"
                        alwaysShowLabel={true}
                      />
                    </div>
                  </div>
                  <div className="tasks-summary-page--trends-chart">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                        style={{ cursor: !selectedUserId || selectedUserId === userId ? 'pointer' : 'default' }}
                      >
                        <XAxis dataKey="name" tickLine={false} tick={{ fill: '#6b7785', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#6b7785', fontSize: 12 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend
                          wrapperStyle={{ paddingTop: '12px' }}
                          iconType="circle"
                          formatter={(value) => <span style={{ color: '#6b7785', fontSize: 13 }}>{value}</span>}
                        />
                        <Bar
                          dataKey="manual"
                          fill="#28a745"
                          name="Manual Tasks"
                          radius={[3, 3, 0, 0]}
                          onClick={(data) => {
                            if (data?.fullDate && (!selectedUserId || selectedUserId === userId)) {
                              let dateParams;
                              if (data.fullDate.length === 7) {
                                const [year, month] = data.fullDate.split('-').map(Number);
                                const lastDay = new Date(year, month, 0).getDate();
                                dateParams = `completedDateStart=${data.fullDate}-01&completedDateEnd=${data.fullDate}-${String(lastDay).padStart(2, '0')}`;
                              } else {
                                dateParams = `completedDate=${data.fullDate}`;
                              }
                              EverTrue.Navigator(`/tasks?status=completed&${dateParams}`, true);
                            }
                          }}
                        />
                        <Bar
                          dataKey="automated"
                          fill="#8e44ad"
                          name="Automated Tasks"
                          radius={[3, 3, 0, 0]}
                          onClick={() => {
                            if (!selectedUserId || selectedUserId === userId) {
                              EverTrue.Navigator('/tasks/automated', true);
                            }
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {selectedTask && (
        <AddTask
          isOpen={taskModalOpen}
          closeModal={() => setTaskModalOpen(false)}
          handleClose={() => setTaskModalOpen(false)}
          taskObj={selectedTask}
        />
      )}
    </div>
  );
};

export default TasksSummaryController;
